
// src/pages/permission/PermissionAssignment.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  InputAdornment,
  Divider,
  Alert
} from '@mui/material';
import { Search, Edit } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../context/PermissionContext';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getUserById, getUsersList, updateUserDirectPermissions } from '../../store/slices/userSlice';
import PageLayout from '../../components/layout/PageLayout';

export default function PermissionAssignment() {
  const dispatch = useDispatch();
  const { user: currentUser, refreshPermissions } = useAuth();
  const { allPermissions, loading: permLoading } = usePermissions();
  

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]); // Array of UUID strings
  const [permissionSearch, setPermissionSearch] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { 'relations[]': ['status', 'roles'] };
      const res = await dispatch(getUsersList(params)).unwrap();
      const list = res?.list ?? (Array.isArray(res) ? res : []);
      setUsers(list);
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Load users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (user) => {
    setSelectedUser(user);
    setOpenDialog(true);
    setDialogLoading(true);
    setSelectedPermissions([]);

    try {
      const res = await dispatch(getUserById(user.id)).unwrap();
      const userDirectPermissions = res?.data?.permissions ?? res?.permissions ?? [];

      // Extract only the UUIDs (permission IDs)
      const uuids = userDirectPermissions
        .map(perm => perm.id)
        .filter(Boolean);

      setSelectedPermissions(uuids);
    } catch (err) {
      console.error('Failed to load user permissions:', err);
      toast.error('Could not load current permissions for this user');
      setSelectedPermissions([]);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setSelectedPermissions([]);
    setPermissionSearch('');
    setDialogLoading(false);
  };

  const handleTogglePermission = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSave = async () => {
    if (!selectedUser || saving) return;

    setSaving(true);

    try {
      const permissionsToSend = selectedPermissions.filter(Boolean);


      await dispatch(updateUserDirectPermissions({
        userId: selectedUser.id,
        permissions: permissionsToSend,
      })).unwrap();

      toast.success(`Permissions updated for ${selectedUser.name || selectedUser.email}`);

      // Critical: If we just updated the current logged-in user → refresh their permissions
      if (selectedUser.id === currentUser?.id) {
        await refreshPermissions?.(); // This updates sidebar + route access instantly
        toast.info('Your permissions have been updated. Some features may now be available.');
      }

      handleCloseDialog();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to update permissions';
      toast.error(msg);
      console.error('Permission save error:', err.response?.data || err);
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const isLoading = loading || permLoading;

  return (
    <PageLayout>
      <Box p={3}>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh" flexDirection="column" gap={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading users and permissions...</Typography>
        </Box>
      ) : (
        <>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Manage User Direct Permissions
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Assign or remove individual permissions that override role-based access.
      </Typography>

      <TextField
        fullWidth
        placeholder="Search users by name or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Paper elevation={3}>
        {filteredUsers.length === 0 ? (
          <Box p={4} textAlign="center" color="text.secondary">
            <Typography>No users found matching your search.</Typography>
          </Box>
        ) : (
          <List>
            {filteredUsers.map((user) => (
              <React.Fragment key={user.id}>
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenDialog(user)}
                      color="primary"
                      size="large"
                    >
                      <Edit />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatar}>
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={500}>
                        {user.name || 'Unnamed User'}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          {user.email}
                        </Typography>
                        <br />
                        <Chip
                          label={user.roles?.[0]?.name || 'No Role'}
                          size="small"
                          color="default"
                          sx={{ mt: 1 }}
                        />
                        {user.id === currentUser?.id && (
                          <Chip label="You" color="primary" size="small" sx={{ ml: 1 }} />
                        )}
                      </>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
        </>
      )}

      {/* Permission Assignment Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Direct Permissions — {selectedUser?.name || selectedUser?.email}
        </DialogTitle>

        <DialogContent dividers sx={{ maxHeight: '60vh' }}>
          {dialogLoading ? (
            <Box textAlign="center" py={6}>
              <CircularProgress />
              <Typography mt={2}>Loading current permissions...</Typography>
            </Box>
          ) : allPermissions.length === 0 ? (
            <Alert severity="info">No permissions defined in the system.</Alert>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Search permissions..."
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <FormGroup>
              {(() => {
                const filtered = allPermissions.filter((perm) => {
                  const q = permissionSearch.trim().toLowerCase();
                  if (!q) return true;
                  const name = (perm.name || '').toLowerCase();
                  const key = (perm.key || '').toLowerCase();
                  return name.includes(q) || key.includes(q);
                });
                if (filtered.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No permissions match &quot;{permissionSearch}&quot;
                    </Typography>
                  );
                }
                return filtered.map((perm) => (
                  <FormControlLabel
                    key={perm.id}
                    control={
                      <Checkbox
                        checked={selectedPermissions.includes(perm.id)}
                        onChange={() => handleTogglePermission(perm.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography fontWeight={500}>{perm.name}</Typography>
                        {perm.key && (
                          <Typography variant="caption" color="text.secondary">
                            {perm.key}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ));
              })()}
              </FormGroup>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || dialogLoading}
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageLayout>
  );
}